import { Code2, Gauge, Loader2, ShieldAlert, Sparkles, Wand2 } from "lucide-react"
import { useState } from "react"

import { Page } from "@/components/layout/page"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { EmptyState } from "@/components/ui/empty-state"
import { Input } from "@/components/ui/input"
import { api } from "@/lib/api"
import type { CodeReview, ReviewCategory, ReviewFinding, ReviewSeverity } from "@/lib/types"
import { cn } from "@/lib/utils"

const MAX_LINES = 300

const CATEGORY: Record<ReviewCategory, { label: string; icon: typeof Code2 }> = {
  security: { label: "Security", icon: ShieldAlert },
  performance: { label: "Performance", icon: Gauge },
  readability: { label: "Readability", icon: Sparkles },
}

const SEVERITY_VARIANT: Record<ReviewSeverity, "destructive" | "warning" | "secondary"> = {
  high: "destructive",
  medium: "warning",
  low: "secondary",
}

const EXAMPLE = `import sqlite3

def find_user(name):
    conn = sqlite3.connect("app.db")
    rows = conn.execute(f"SELECT * FROM users WHERE name = '{name}'").fetchall()
    result = []
    for r in rows:
        result.append(r)
    return result
`

/**
 * Code review, for the technical round of an interview.
 *
 * Paste a function from a take-home task or your own project and get the
 * review a senior engineer would give it: security first, then performance,
 * then readability, each finding pointing at a line. Secrets are masked on
 * the server before the code reaches a model, and nothing is stored
 * (`tools/code_review.py`).
 */
export function CodeReviewRoute() {
  const [code, setCode] = useState("")
  const [language, setLanguage] = useState("")
  const [review, setReview] = useState<CodeReview | null>(null)
  const [reviewed, setReviewed] = useState("")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Counted as the server counts them: trailing blank lines are not code.
  const trimmed = code.replace(/\n+$/, "")
  const lineCount = trimmed ? trimmed.split("\n").length : 0
  const tooLong = lineCount > MAX_LINES

  async function run() {
    setBusy(true)
    setError(null)
    try {
      setReview(await api.codeReview(code, language.trim()))
      setReviewed(code)
    } catch (reason) {
      setError((reason as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Page
      tone="work"
      plain
      eyebrow="Technical round"
      title="Code review"
      description="Paste a snippet and get the review a senior engineer would give it – security, performance, readability."
    >
      <div className="grid gap-5 px-5 py-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:px-8">
        <section className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              aria-label="Language (optional)"
              placeholder="Language (optional) – e.g. Python"
              className="max-w-[260px]"
              value={language}
              maxLength={40}
              onChange={(event) => setLanguage(event.target.value)}
            />
            <Button variant="ghost" size="sm" onClick={() => setCode(EXAMPLE)}>
              <Wand2 className="size-3.5" aria-hidden />
              Try an example
            </Button>
          </div>
          <textarea
            aria-label="Code to review"
            spellCheck={false}
            value={code}
            onChange={(event) => setCode(event.target.value)}
            placeholder="Paste a function or a file, up to 300 lines…"
            className="min-h-[420px] w-full resize-y rounded-xl border bg-card/40 p-3 font-mono text-[13px] leading-relaxed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={() => void run()} disabled={busy || !code.trim() || tooLong}>
              {busy ? (
                <Loader2 className="animate-spin" aria-hidden />
              ) : (
                <Code2 aria-hidden />
              )}
              {busy ? "Reviewing…" : "Review my code"}
            </Button>
            <span className={cn("text-xs", tooLong ? "text-destructive" : "text-muted-foreground")}>
              {lineCount} / {MAX_LINES} lines
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            Passwords and keys are masked before the code is sent to the model, and nothing is
            stored. One model call per review. An AI review is a second pair of eyes, not a
            security scanner.
          </p>
          {error ? (
            <p className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          ) : null}
        </section>

        <section className="space-y-3">
          {review ? (
            <ReviewView review={review} code={reviewed} />
          ) : (
            <EmptyState
              icon={Code2}
              title="No review yet"
              description="Paste some code – or try the example – and the findings appear here, most serious first."
            />
          )}
        </section>
      </div>
    </Page>
  )
}

function ReviewView({ review, code }: { review: CodeReview; code: string }) {
  const lines = code.split("\n")
  const counts = (["high", "medium", "low"] as ReviewSeverity[]).map((severity) => ({
    severity,
    count: review.findings.filter((finding) => finding.severity === severity).length,
  }))

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border bg-card/50 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline">{review.language}</Badge>
          <span className="text-xs text-muted-foreground">{review.lines} lines</span>
          {counts.map(({ severity, count }) =>
            count ? (
              <Badge key={severity} variant={SEVERITY_VARIANT[severity]}>
                {count} {severity}
              </Badge>
            ) : null,
          )}
        </div>
        {review.summary ? <p className="mt-2 text-sm leading-relaxed">{review.summary}</p> : null}
        {review.masked.length > 0 ? (
          <p className="mt-2 text-xs text-muted-foreground">
            Masked before review: {[...new Set(review.masked)].join(", ")}.
          </p>
        ) : null}
      </div>

      {review.findings.length === 0 ? (
        <p className="rounded-lg border bg-card/50 px-4 py-3 text-sm">
          No problems found. That is a reviewer&apos;s opinion, not a guarantee.
        </p>
      ) : (
        <ol className="space-y-3">
          {review.findings.map((finding, index) => (
            <FindingCard
              key={`${index}-${finding.title}`}
              finding={finding}
              source={finding.line ? lines[finding.line - 1] : undefined}
            />
          ))}
        </ol>
      )}
    </div>
  )
}

function FindingCard({ finding, source }: { finding: ReviewFinding; source?: string }) {
  const { label, icon: Icon } = CATEGORY[finding.category]
  return (
    <li className="space-y-2 rounded-2xl border bg-card/50 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={SEVERITY_VARIANT[finding.severity]}>{finding.severity}</Badge>
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          <Icon className="size-3.5" aria-hidden />
          {label}
        </span>
        {finding.line ? (
          <span className="font-mono text-xs text-muted-foreground">line {finding.line}</span>
        ) : null}
      </div>
      <p className="font-medium">{finding.title}</p>
      {source !== undefined ? (
        <pre className="overflow-x-auto rounded-md bg-muted px-3 py-1.5 font-mono text-xs">
          <span className="mr-3 select-none text-muted-foreground">{finding.line}</span>
          {source}
        </pre>
      ) : null}
      {finding.explanation ? <p className="text-sm">{finding.explanation}</p> : null}
      {finding.suggestion ? (
        <div>
          <p className="text-xs font-medium text-muted-foreground">Suggested fix</p>
          <pre className="mt-1 overflow-x-auto whitespace-pre-wrap rounded-md border bg-background/60 px-3 py-2 font-mono text-xs">
            {finding.suggestion}
          </pre>
        </div>
      ) : null}
    </li>
  )
}
